import { defineComponent as _defineComponent } from 'vue'
import {
  IPropList,
  allTableHeaderList,
  headPropList,
} from '@/components/materialTable/src/config/tableConfig'
import store from '@/store'
import { updateLocalPreference } from '@/store/modules/preferencesSetting'
import { translate } from '@/util/commonFun'
import { genFinalRecord } from '@/util/commonFuncs'
import { operateRecordTableType } from '@/util/configure'
import sortable from 'sortablejs'
import { computed, nextTick, ref, watch, withDefaults } from 'vue'

import { headerList, lblHeaderList } from './data'

type ISortTable = ReturnType<typeof sortable['create']>
type ITable = typeof headerList
type SaveDataColumn = ITable[number]

/** vuex 偏好设置 */

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    tableHeaders: { default: () => headPropList }
  },
  emits: ['onRestFilter'],
  setup(__props: any, { expose, emit: emits }) {

const props = __props as { tableHeaders: IPropList[] }



const preferencesSetting = computed(
  () => store.state.preferencesSetting?.setting
)

// 是否显示重置线
const isShowResizableLine = ref(false)
// 记录元素
const headerRef = ref<HTMLElement>()
const optionsRef = ref<HTMLElement>()
// 记录创建返回
const headerSort = ref<ISortTable>()
const optionsSort = ref<ISortTable>()
// 源数据
const oriTableHeaderData = ref<ITable>(
  store.state.userInfo?.platform === 7
    ? [
        ...JSON.parse(JSON.stringify(headerList)),
        ...JSON.parse(JSON.stringify(lblHeaderList)),
      ]
    : JSON.parse(JSON.stringify(headerList))
)
// 记录需要保存的表头信息
const needSaveData = ref<ITable>(
  sortTableData(props.tableHeaders.map((it) => it.prop))
)
// 重置线div元素
const resizableLine = ref<HTMLDivElement | null>(null)
const currentTarget = ref<HTMLDivElement>()
const currentLocation = ref(0)
const currentField = ref('')
const containerRef = ref<HTMLDivElement | null>()
// 容器在窗口的位置
const offsetX = ref(0)
//判断是否点击取消按钮
const isShowDialog = ref(false)
const selectedProp = ref(null)
const isConfirmSave = ref(false)
//记录列能否删除
const removeState = ref(false)
//定义不能删除数据
const notRemoveList = [
  'matCode',
  'texture',
  'thick',
  'specHeight',
  'specWidth',
  'amount',
  'texDir',
  'select',
]

//记录变化后的宽度
const recordChangeAfterWidth = ref('')
// 当前正在编辑的自定义表头（按 prop 区分）
const editingCustomLabelProp = ref<string | null>(null)
// 每一列自己的自定义表头错误信息
const customLabelErrorMap = ref<Record<string, string>>({})
// 自定义表头输入框 ref（按 prop 存储）
const customLabelInputRefMap = ref<Record<string, any>>({})
// 可选项数据
const optionsData = computed(() => {
  const d = oriTableHeaderData.value
    .filter(
      (it) =>
        !it.require &&
        it.prop !== 'select' &&
        !needSaveData.value?.find((item) => item?.prop === it.prop)
    )
    .map((da) => {
      const re = { ...da }
      const findDa = allTableHeaderList.find((p: any) => p.prop == da.prop)
      re.label = findDa ? findDa.label : da.label
      return re
    })
  return d
})

const resetDataList = JSON.parse(JSON.stringify(headerList)).splice(0, 19)
watch(
  () => preferencesSetting.value?.needSaveData,
  (n) => {
    if (n) {
      // 做一层拷贝避免未保存的情况下覆盖了数据
      needSaveData.value = JSON.parse(JSON.stringify(n))
    }
  },
  { immediate: true, deep: true }
)
// 根据 prop 字段筛选需要的表头数据
function sortTableData(props: string[]) {
  const headerPool = oriTableHeaderData.value
  return props
    .map((key) => headerPool.find((it) => it.prop === key))
    .filter((item): item is SaveDataColumn => Boolean(item))
}
// 更新表格数据
function updateTable(sortInstance?: ISortTable) {
  const dataList = sortInstance?.toArray() ?? []
  const oldMap = new Map(needSaveData.value.map((it) => [it.prop, it]))
  needSaveData.value = sortTableData(dataList).map((item) => {
    const old = oldMap.get(item.prop)
    if (old?.customLabel !== undefined) {
      return { ...item, customLabel: old.customLabel }
    }
    return item
  })
}
// 删除元素
function handleDeleteProp(field: string) {
  if (!notRemoveList.includes(field)) {
    needSaveData.value = needSaveData.value.filter((it) => it.prop !== field)
    removeState.value = true
  } else {
    removeState.value = false
  }
}
function translateLang(key?: string) {
  return key ? translate(key) : ''
}
function showEditTableHeader() {
  nextTick(() => {
    if (headerRef.value && optionsRef.value) {
      headerSort.value = sortable.create(headerRef.value, {
        animation: 150,
        group: {
          name: 'shared',
          put: false,
        },
        sort: false,
        setData,
      })
      optionsSort.value = sortable.create(optionsRef.value, {
        animation: 150,
        handle: '.my-span',
        group: {
          name: 'shared',
        },
        // 其他列表单元添加到本列表容器的回调函数
        onAdd() {
          updateTable(optionsSort.value)
        },
        // 列表单元在列表容器中的排序发生变化后的回调函数
        onUpdate() {
          updateTable(optionsSort.value)
        },
        setData,
      })
    }
  })
}
function setData(dataTransfer: DataTransfer, dragEl: HTMLElement) {
  dataTransfer.setData('id', dragEl?.dataset?.id ?? '')
}

// 打开modal弹窗
function handleOpenModal() {
  isShowDialog.value = true
}

function handleCancel() {
  isShowDialog.value = false
  editingCustomLabelProp.value = null
  customLabelErrorMap.value = {}
  // 组件不会销毁,在组件关闭时需要同步 vuex 中保存的数据避免错误
  needSaveData.value = JSON.parse(
    JSON.stringify(preferencesSetting.value?.needSaveData)
  )
}

function handleReset() {
  needSaveData.value = resetDataList
}
function handleClick(prop: any) {
  selectedProp.value = prop
}

// 点击自定义表头内容进入编辑
function handleCustomLabelClick(item: {
  prop: string
  customLabel?: string
  label?: string
}) {
  editingCustomLabelProp.value = item.prop
  nextTick(() => {
    const inputComp = customLabelInputRefMap.value[item.prop]
    // a-input 组件：通过 $el 拿到原生 input 聚焦
    const el = inputComp?.$el ?? inputComp
    const nativeInput = el?.querySelector?.('input') ?? el
    nativeInput?.focus?.()
  })
}

function setCustomLabelInputRef(el: any, prop: string) {
  if (!prop) return
  if (el) {
    customLabelInputRefMap.value[prop] = el
    return
  }
  delete customLabelInputRefMap.value[prop]
}

function getCustomLabelInputRefSetter(prop: string) {
  return (el: any) => {
    setCustomLabelInputRef(el, prop)
  }
}

function getCustomLabelErrorMessage(newLabel: string, currentProp: string) {
  customLabelErrorMap.value[currentProp] = ''
  const trimmedLabel = newLabel.trim()
  if (!trimmedLabel) {
    customLabelErrorMap.value[currentProp] = translateLang(
      'materialPage.settingHead.headerNameEmpty'
    )
    return
  }

  // 与其它列自定义表名重复
  const isDuplicateWithCustomLabel = needSaveData.value.some((it) => {
    if (it.prop === currentProp) {
      return false
    }
    return (translateLang(it.customLabel) ?? '').trim() === trimmedLabel
  })
  if (isDuplicateWithCustomLabel) {
    customLabelErrorMap.value[currentProp] = translateLang(
      'materialPage.settingHead.duplicateHeader'
    )
  }

  // 当前输入与其它列原始表头重复
  const isDuplicateWithLabel = needSaveData.value.some((it) => {
    if (it.prop === currentProp) {
      return false
    }
    return (translateLang(it.label) ?? '').trim() === trimmedLabel
  })
  if (isDuplicateWithLabel) {
    customLabelErrorMap.value[currentProp] = translateLang(
      'materialPage.settingHead.duplicateHeader'
    )
  }
}

// 失焦时校验并在通过后保存
function handleCustomLabelBlur(item: SaveDataColumn) {
  handleCustomLabelConfirm(item)
}

// 确认保存自定义表头名并退出编辑
function handleCustomLabelConfirm(item?: SaveDataColumn) {
  const currentProp = item?.prop ?? editingCustomLabelProp.value
  if (!currentProp) return
  const target = needSaveData.value.find((it) => it.prop === currentProp)
  if (!target) return

  const newLabel = target.customLabel ?? ''
  getCustomLabelErrorMessage(newLabel, currentProp)
  if (customLabelErrorMap.value[currentProp]) {
    return
  }

  target.customLabel = newLabel.trim()
  editingCustomLabelProp.value = null
}

function handleCustomLabelInput(event: Event, item: SaveDataColumn) {
  item.customLabel = (event.target as HTMLInputElement)?.value ?? ''
  editingCustomLabelProp.value = item.prop
  getCustomLabelErrorMessage(item.customLabel, item.prop)
}

async function handleOk() {
  isShowDialog.value = false
  isConfirmSave.value = true
  needSaveData.value = needSaveData.value.filter((item: any) => item)
  updateLocalPreference({ needSaveData: needSaveData.value })
  emits('onRestFilter')
  // 生成操作记录
  let recordData = {
    key: '',
    operateArr: [
      {
        key: '',
        dataArr: [
          {
            type: 'table',
            list: needSaveData.value.map((item) => ({
              ...item,
              customLabel: translate(item.customLabel as string),
              label: translate(item.label as string),
            })),
            oriList: props.tableHeaders.map((item) => ({
              ...item,
              customLabel: translate(item.customLabel as string),
              label: translate(item.label as string),
            })),
            keys: ['label', 'customLabel', 'temp'],
            tableType: operateRecordTableType.MATERIAL_LIST_TABLE_HEADER,
            key: '自定义开料清单表头',
            idKey: 'prop',
          },
        ],
      },
    ],
  }
  genFinalRecord(recordData, '开料清单', null, null)
}

// 控制宽度
function handleMouseResizableDown(e: MouseEvent, headItem: any) {
  const { prop } = headItem
  isShowResizableLine.value = true

  // 保存当前点击字段
  currentField.value = prop
  // 保存点击的元素
  currentTarget.value = e.target as HTMLDivElement
  currentLocation.value = e.clientX
  // 获取整个容器在窗口的位置

  const rect = containerRef.value?.getBoundingClientRect()

  offsetX.value = rect?.left ?? 0
  changeResizableLocation()
}
// 鼠标移动
function handleContainerMove(e: MouseEvent) {
  if (!isShowResizableLine.value) return
  const { clientX } = e

  const offset = clientX - currentLocation.value

  changeResizableLocation(offset)
}

// // 鼠标抬起
function handleContainerUp(e: MouseEvent) {
  if (!isShowResizableLine.value) return
  const target = needSaveData.value.find((it) => it.prop === currentField.value)
  if (!target) return
  const { clientX } = e
  const offset = clientX - currentLocation.value

  const width = +target.width.replace('px', '') + offset

  // 最小宽度
  const minWidth = +target.minWidth.replace('px', '')
  // 最大宽度
  const maxWidth = +target.maxWidth.replace('px', '')

  // 最终宽度
  const finalWidth = computeFinalWidth(width, minWidth, maxWidth)

  recordChangeAfterWidth.value = `${finalWidth}px`
  target.width = `${finalWidth}px`

  isShowResizableLine.value = false
}
// 变更重置线位置
function changeResizableLocation(offset = 0) {
  const el = resizableLine.value

  const rect = currentTarget.value?.getBoundingClientRect()
  if (el && rect) {
    // 元素宽度
    const elWidth = (currentTarget.value?.offsetWidth ?? 0) / 2
    // 需要移动的距离
    const lineOffset = rect.left + elWidth - offsetX.value + offset
    el.style.left = lineOffset + 'px'
  }
}

function computeFinalWidth(width: number, minWidth: number, maxWidth: number) {
  if (width <= minWidth) {
    return minWidth
  } else if (width >= maxWidth) {
    return maxWidth
  } else {
    return width
  }
}

expose({
  openModal: handleOpenModal,
  showEditTableHeader,
})

return { __sfc: true,props, preferencesSetting, isShowResizableLine, headerRef, optionsRef, headerSort, optionsSort, oriTableHeaderData, needSaveData, resizableLine, currentTarget, currentLocation, currentField, containerRef, offsetX, isShowDialog, selectedProp, isConfirmSave, removeState, notRemoveList, emits, recordChangeAfterWidth, editingCustomLabelProp, customLabelErrorMap, customLabelInputRefMap, optionsData, resetDataList, sortTableData, updateTable, handleDeleteProp, translateLang, showEditTableHeader, setData, handleOpenModal, handleCancel, handleReset, handleClick, handleCustomLabelClick, setCustomLabelInputRef, getCustomLabelInputRefSetter, getCustomLabelErrorMessage, handleCustomLabelBlur, handleCustomLabelConfirm, handleCustomLabelInput, handleOk, handleMouseResizableDown, handleContainerMove, handleContainerUp, changeResizableLocation, computeFinalWidth }
}

})